import styled from 'styled-components';
import type { PrcVersion } from '@shared/api';
import { fleetLeading, fleetType, typo } from '@entities/fleet';
import { formatIsoDate, prcSourceLabel } from '@entities/prc';
import { formatDay, personName } from '../format';
import { NARROW } from '../ui';

/**
 * Маршрут версии: расчёт → на утверждении → решение. Каждый шаг — кто и когда;
 * текущий выделен. Читается как штамп согласования на листе книги, а не как
 * декоративный «прогресс».
 */

type StepState = 'done' | 'current' | 'pending' | 'returned';

interface Step {
  title: string;
  state: StepState;
  who: string | null;
  when: string | null;
  note: string | null;
}

const List = styled.ol`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin: ${({ theme }) => `${theme.spacing(4)} 0 0`};
  padding: 0;
  list-style: none;
  counter-reset: step;

  @container prc (max-width: ${NARROW}) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Item = styled.li<{ $state: StepState }>`
  position: relative;
  min-width: 0;
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(4)} ${theme.spacing(3)} 0`};
  border-top: 2px solid
    ${({ theme, $state }) =>
      $state === 'done'
        ? theme.colors.text.secondary
        : $state === 'current'
          ? theme.colors.primary
          : $state === 'returned'
            ? theme.colors.status.warning
            : theme.colors.border};
  counter-increment: step;

  & > .title {
    display: flex;
    align-items: baseline;
    gap: ${({ theme }) => theme.spacing(2)};
    margin: 0;
    font-size: ${fleetType.body};
    font-weight: 600;
    line-height: ${fleetLeading.tight};
    color: ${({ theme, $state }) =>
      $state === 'pending' ? theme.colors.text.secondary : theme.colors.text.primary};
  }

  & > .title::before {
    content: counter(step, decimal-leading-zero);
    font-family: ${({ theme }) => theme.font.mono};
    font-size: ${fleetType.micro};
    font-weight: 400;
    color: ${({ theme, $state }) =>
      $state === 'current' ? theme.colors.primary : theme.colors.text.secondary};
  }

  & > .meta {
    margin: ${({ theme }) => `${theme.spacing(1)} 0 0`};
    font-size: ${fleetType.small};
    line-height: ${fleetLeading.normal};
    color: ${({ theme }) => theme.colors.text.secondary};
    overflow-wrap: anywhere;
    text-wrap: pretty;
  }

  @container prc (max-width: ${NARROW}) {
    padding-right: 0;
  }
`;

function steps(v: PrcVersion): Step[] {
  const submitted = v.submitted_at !== null;
  const rejectedLast =
    v.rejected_at !== null && (v.submitted_at === null || v.rejected_at >= v.submitted_at);
  const status = v.status;
  const editable = status === 'draft' || status === 'rejected';
  const first: Step = {
    title: editable && rejectedLast ? 'Доработка' : 'Расчёт',
    state: editable ? 'current' : 'done',
    who: personName(v.created_by_name),
    when: v.created_at,
    note: prcSourceLabel(v.source),
  };
  const second: Step = {
    title: 'На утверждении',
    state:
      status === 'submitted' ? 'current' : submitted && status !== 'draft' ? 'done' : 'pending',
    who: submitted ? personName(v.submitted_by_name) : null,
    when: submitted && status !== 'draft' ? v.submitted_at : null,
    note:
      submitted && status !== 'draft'
        ? null
        : rejectedLast
          ? 'после доработки — отправить снова'
          : 'ещё не отправлена',
  };
  let third: Step;
  if (status === 'approved' || status === 'superseded') {
    third = {
      title: status === 'superseded' ? 'Утверждена, затем заменена' : 'Утверждена',
      state: status === 'approved' ? 'current' : 'done',
      who: personName(v.approved_by_name),
      when: v.approved_at,
      note: v.valid_from ? `действует с ${formatIsoDate(v.valid_from)}` : null,
    };
  } else if (status === 'rejected' || (status === 'draft' && rejectedLast)) {
    third = {
      title: 'Возвращена на доработку',
      state: 'returned',
      who: personName(v.rejected_by_name),
      when: v.rejected_at,
      note: null,
    };
  } else {
    third = {
      title: 'Утверждение',
      state: 'pending',
      who: null,
      when: null,
      note: 'ещё нет решения',
    };
  }
  return [first, second, third];
}

export function VersionRoute({ version }: { version: PrcVersion }) {
  return (
    <List aria-label="Маршрут версии">
      {steps(version).map((s) => (
        <Item
          key={s.title}
          $state={s.state}
          aria-current={s.state === 'current' ? 'step' : undefined}
        >
          <p className="title">{s.title}</p>
          <p className="meta">
            {typo(
              [s.who, s.when ? formatDay(s.when) : null, s.note].filter(Boolean).join(' · ') || '—',
            )}
          </p>
        </Item>
      ))}
    </List>
  );
}
